import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  Grid,
  TextField,
  Button,
  Box,
  Paper,
  Typography,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {Autocomplete} from '@mui/material';
// import {DateRangePicker, LocalizationProvider} from '@mui/lab';
// import AdapterDateFns from '@mui/lab/AdapterDateFns'; // Make sure you're using DateFns

const FilterCard = ({
  dataSources,
  filters,
  setFilters,
  onApply,
  handleDateRangeChange,
  handleSearchChange,
  onClear,
  handleChange,
  disableDataSource,
}) => {
  // Effect to handle disabling Data Source
  useEffect(() => {
    if (disableDataSource) {
      setFilters((prev) => ({
        ...prev,
        dataSource: '',
      }));
    }
  }, [disableDataSource]);

  return (
    <Paper elevation={3} sx={{padding: 2, marginBottom: 2}}>
      <Grid container spacing={2} alignItems='center'>
        {/* Search Section */}
        <Grid item xs={12}>
          <Typography variant='h6' gutterBottom>
            Search
          </Typography>
          <TextField
            label='Search'
            value={filters.search}
            onChange={handleSearchChange}
            placeholder='Search by ID, Name, Email, Phone, Marks, MPC_4, or Other Data content'
            fullWidth
          />
        </Grid>

        {/* Date Range Section */}
        <Grid item xs={12}>
          <Typography variant='h6' gutterBottom>
            Created Date Range
          </Typography>
          <Box sx={{display: 'flex', gap: 2}}>
            <TextField
              label='From Date'
              type='date'
              value={filters.dateRange?.[0] || ''}
              onChange={(e) =>
                handleDateRangeChange([
                  e.target.value,
                  filters.dateRange?.[1] || null,
                ])
              }
              fullWidth
              InputLabelProps={{shrink: true}}
            />
            <TextField
              label='To Date'
              type='date'
              value={filters.dateRange?.[1] || ''}
              onChange={(e) =>
                handleDateRangeChange([
                  filters.dateRange?.[0] || null,
                  e.target.value,
                ])
              }
              fullWidth
              InputLabelProps={{shrink: true}}
            />
          </Box>
        </Grid>

        {/* Country TextField Section */}
        <Grid item xs={12} sm={6} md={6}>
          <Typography variant='h6' gutterBottom>
            Country
          </Typography>
          <TextField
            fullWidth
            label='Country'
            value={filters.country}
            onChange={(e) => setFilters({...filters, country: e.target.value})}
            placeholder='Enter country name'
          />
        </Grid>

        {/* Data Source Dropdown Section */}
        <Grid item xs={12} sm={6} md={6}>
          <Typography variant='h6' gutterBottom>
            Data Source
          </Typography>
          <Autocomplete
            disablePortal
            options={dataSources}
            getOptionLabel={(option) => option.label}
            value={
              dataSources.find((s) => s.key === filters.dataSource) || null
            }
            onChange={handleChange('dataSource')}
            renderInput={(params) => (
              <TextField
                {...params}
                label='Data Source'
                fullWidth
                disabled={disableDataSource}
              />
            )}
          />
        </Grid>

        {/* Integration Source Filter */}
        <Grid item xs={12} sm={6} md={6}>
          <Typography variant='h6' gutterBottom>
            Integration Source
          </Typography>
          <TextField
            fullWidth
            label='Integration Source'
            value={filters.integrationSource || ''}
            onChange={(e) => setFilters({...filters, integrationSource: e.target.value})}
            placeholder='Enter integration source'
          />
        </Grid>

        {/* New Data Filter Section */}
        <Grid item xs={12}>
          <Typography variant='h6' gutterBottom>
            Data Type
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.showNewData || false}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    showNewData: e.target.checked,
                  }))
                }
                color='primary'
              />
            }
            label='New data from APIs'
          />
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box display='flex' justifyContent='flex-end' gap={2}>
            <Button variant='contained' color='primary' onClick={onApply}>
              Apply Filters
            </Button>
            <Button variant='outlined' color='secondary' onClick={onClear}>
              Clear Filters
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

// Updated PropTypes to reflect changes
FilterCard.propTypes = {
  dataSources: PropTypes.array.isRequired,
  filters: PropTypes.shape({
    country: PropTypes.string,
    dataSource: PropTypes.string,
    integrationSource: PropTypes.string,
    search: PropTypes.string,
    dateRange: PropTypes.arrayOf(PropTypes.instanceOf(Date)), // Expecting JavaScript Date objects
    showNewData: PropTypes.bool,
  }).isRequired,
  setFilters: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  disableDataSource: PropTypes.bool,
  handleSearchChange: PropTypes.func.isRequired,
  handleDateRangeChange: PropTypes.func.isRequired,
};

export default FilterCard;
